import {remove, sortedUniq} from 'lodash';
import React, {useMemo, useState, useCallback, useEffect} from 'react';

import LabelMenu from './LabelMenu';
import {TestRegionToolbar} from './TestRegionToolbar';
import {StudioContentLayout} from '../../../base-components/StudioContentLayout';
import {MenuListLocation} from '../../../base-components/StudioAnnotator/MenuList';
import {AnnotatorProvider} from '../../../base-components/StudioAnnotator/Provider';
import Annotator, {
  ToolType,
  STAGE_HEIGHT_ADJ,
} from '../../../base-components/StudioAnnotator/Annotator';
import {
  BoxLabelProps,
  LineLabelProps,
  PolyLabelProps,
} from '../../../base-components/StudioAnnotator/types';

import './TestRegionSelection.scss';

export const ZOOM_PAD_VERTICAL = 5;
export const ZOOM_PAD_HORIZONTAL = 5;

export type Regions = {
  lines?: LineLabelProps[];
  boxes?: BoxLabelProps[];
  polygons?: PolyLabelProps[];
};

export type TestRegionSelectionProps = {
  ids: string[];
  regions: Regions;

  status?: string;
  disabled?: boolean;
  hideMissingFields?: boolean;

  source: React.ReactNode;
  onReset?: () => void;
  onChange: (regions: Regions) => void;
};

export interface LabelInterface {
  id: string;
  label?: string;
}

export function TestRegionSelection({
  source,
  status,
  regions,
  disabled = false,
  onReset = () => {},
  onChange = () => {},
}: TestRegionSelectionProps) {
  const [tool, setTool] = useState<ToolType>('SELECT');
  const [selections, setSelections] = useState<string[]>([]);
  const [labelMenuOpen, setlabelMenuOpen] = useState<boolean>(false);
  const [labelMenuLoc, setlabelMenuLoc] = useState<MenuListLocation>({x: 0, y: 0});

  const [boxes, setBoxes] = useState<BoxLabelProps[]>(regions?.boxes || []);
  const [lines, setLines] = useState<LineLabelProps[]>(regions?.lines || []);
  const [polys, setPolys] = useState<PolyLabelProps[]>(regions?.polygons || []);

  const oldLabels: string[] = useMemo(
    () => [...lines, ...polys, ...boxes].map(item => item?.label || ''),
    [lines, polys, boxes]
  );

  const [category, setCategory] = useState<string[]>(
    oldLabels.filter(label => label !== '')
  );

  const onLineLabelCreated = useCallback(
    (line: LineLabelProps) => setLines([...lines, line]),
    [lines]
  );
  const onPolyRegionCreated = useCallback(
    (poly: PolyLabelProps) => setPolys([...polys, poly]),
    [polys]
  );
  const onSectionCreated = useCallback(
    (newRegion: BoxLabelProps) => setBoxes([...boxes, newRegion]),
    [boxes]
  );

  const reset = useCallback(() => {
    setLines([]);
    setBoxes([]);
    setPolys([]);
    setCategory([]);
  }, []);

  /**
   * reset when component unmounts
   */
  useEffect(
    () => () => {
      reset();
    },
    [reset]
  );

  useEffect(() => {
    onChange({
      boxes,
      lines,
      polygons: polys,
    });
  }, [boxes, polys, lines, onChange]);

  const onSectionDelete = useCallback(
    (id: string, type?: ToolType) => {
      switch (type) {
        case 'LINE':
          setLines(lines.filter(line => line.id !== id));
          break;
        case 'POLY':
          setPolys(polys.filter(poly => poly.id !== id));
          break;
        case 'RECT':
          setBoxes(boxes.filter(box => box.id !== id));
          break;
        default:
          return undefined;
      }
    },
    [lines, polys, boxes]
  );

  const onSelect = useCallback(
    (selectedIds: string[]) => setSelections([...selectedIds]),
    []
  );

  const onContextMenu = useCallback((event: PointerEvent) => {
    setlabelMenuOpen(true);
    setlabelMenuLoc({x: event.x, y: event.y});
  }, []);

  const onSectionChange = useCallback(
    (target: BoxLabelProps, value: Partial<BoxLabelProps>) =>
      setBoxes(boxes.map(box => (box.id === target.id ? {...box, ...value} : box))),
    [boxes]
  );

  const updateItemLabel = useCallback(
    (
      label: string,
      arr: any[],
      item?: LineLabelProps | PolyLabelProps | BoxLabelProps
    ) => {
      if (item && arr.length) {
        const isoArr = remove(arr, el => el.id !== item.id);

        return [...isoArr, {...item, label}];
      }

      return [...arr];
    },
    []
  );

  const onCategorySelect = useCallback(
    (val: string) => {
      setCategory(sortedUniq([...category, val]));
      setlabelMenuOpen(false);

      if (selections.length) {
        const selection = selections[0];
        const elType: string = selection.charAt(0);

        switch (elType) {
          case 'L':
            setLines(
              updateItemLabel(
                val,
                lines,
                lines.find(el => el.id === selection)
              )
            );
            break;
          case 'R':
            setBoxes(
              updateItemLabel(
                val,
                boxes,
                boxes.find(el => el.id === selection)
              )
            );
            break;
          case 'P':
            setPolys(
              updateItemLabel(
                val,
                polys,
                polys.find(el => el.id === selection)
              )
            );
            break;
          default: {
          }
        }
      }
    },
    [category, lines, boxes, polys, selections, updateItemLabel]
  );

  return (
    <StudioContentLayout
      className="test-region-selection__container"
      header={
        <TestRegionToolbar
          status={status}
          activeTool={tool}
          disabled={disabled}
          onChangeActiveTool={setTool}
          onReset={() => {
            reset();
            onReset();
          }}
        />
      }
    >
      {({width = 0, height = 0}) => (
        <AnnotatorProvider>
          <>
            {source}

            <Annotator
              tool={tool}
              width={width}
              boxLabels={boxes}
              polyLabels={polys}
              lineLabels={lines}
              zoomEnable={false}
              fullToolset={true}
              selectedLabels={selections}
              height={height + STAGE_HEIGHT_ADJ}
              onSelect={onSelect}
              onChange={onSectionChange}
              onDelete={onSectionDelete}
              onContextMenu={onContextMenu}
              onBoxLabelCreated={onSectionCreated}
              onPolyLabelCreated={onPolyRegionCreated}
              onLineLabelCreated={onLineLabelCreated}
            />
            <LabelMenu
              items={category}
              open={labelMenuOpen}
              location={labelMenuLoc}
              onSelect={onCategorySelect}
              onClose={() => setlabelMenuOpen(false)}
            />
          </>
        </AnnotatorProvider>
      )}
    </StudioContentLayout>
  );
}
